import { useState, useEffect, useRef, useMemo } from 'react';

interface CustomSelectProps {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
  placeholder?: string;
  icon?: React.ReactNode;
  searchable?: boolean;
  renderOption?: (option: string) => React.ReactNode;
  showLabel?: boolean;
  multi?: boolean;
  sortSelectedFirst?: boolean;
  renderMultiDisplay?: (selected: string[]) => React.ReactNode;
  disabled?: boolean;
}

export function CustomSelect({ label, value, options, onChange, placeholder = 'All', icon, searchable = false, renderOption, showLabel = true, multi = false, sortSelectedFirst = false, renderMultiDisplay, disabled = false }: CustomSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Parse comma-separated value into a Set for multi-select
  const selectedSet = useMemo(() => {
    if (!multi || !value) return new Set<string>();
    return new Set(value.split(','));
  }, [multi, value]);

  // Filter options based on search query
  const filteredOptions = useMemo(() => {
    if (!searchable || !searchQuery) return options;
    const query = searchQuery.toLowerCase();
    return options.filter((opt) => {
      if (opt.toLowerCase().includes(query)) return true;
      if (renderOption) {
        const rendered = renderOption(opt);
        if (typeof rendered === 'string' && rendered.toLowerCase().includes(query)) {
          return true;
        }
      }
      return false;
    });
  }, [options, searchQuery, searchable, renderOption]);

  const allOptions = useMemo(() => {
    if (!sortSelectedFirst || !multi || selectedSet.size === 0) return filteredOptions;
    return [...filteredOptions].sort((a, b) => {
      const aSelected = selectedSet.has(a) ? 0 : 1;
      const bSelected = selectedSet.has(b) ? 0 : 1;
      return aSelected - bSelected;
    });
  }, [filteredOptions, sortSelectedFirst, multi, selectedSet]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      searchInputRef.current.focus();
    }
    if (!isOpen) {
      setSearchQuery('');
    }
  }, [isOpen, searchable]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          // This Escape closes the dropdown and nothing else: a dialog hosting
          // the select listens on `window`, after this, and would close too.
          e.stopPropagation();
          setIsOpen(false);
          setFocusedIndex(-1);
          break;
        case 'ArrowDown':
          e.preventDefault();
          setFocusedIndex((prev) => {
            const next = prev + 1;
            return next >= allOptions.length ? 0 : next;
          });
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusedIndex((prev) => {
            const next = prev - 1;
            return next < 0 ? allOptions.length - 1 : next;
          });
          break;
        case 'Enter':
          e.preventDefault();
          if (focusedIndex >= 0) {
            handleSelect(allOptions[focusedIndex]);
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, focusedIndex, allOptions, onChange, multi, value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Set initial focused index when dropdown opens
  const handleOpenDropdown = () => {
    setIsOpen(true);
    if (multi) {
      setFocusedIndex(0);
    } else {
      const currentIndex = allOptions.findIndex(opt => opt === value);
      setFocusedIndex(currentIndex >= 0 ? currentIndex : 0);
    }
  };

  const handleSelect = (option: string) => {
    if (multi) {
      // Toggle the option in comma-separated value
      const newSet = new Set(selectedSet);
      if (newSet.has(option)) {
        newSet.delete(option);
      } else {
        newSet.add(option);
      }
      onChange([...newSet].join(','));
      // Keep dropdown open in multi mode
    } else {
      // Clicking the already-selected option deselects it
      onChange(option === value ? '' : option);
      setIsOpen(false);
      setFocusedIndex(-1);
    }
  };

  const isSelected = (option: string) => {
    if (multi) return selectedSet.has(option);
    return value === option;
  };

  // Display value for the button
  const displayValue = multi
    ? (selectedSet.size === 0
        ? placeholder
        : selectedSet.size === 1
          ? [...selectedSet][0]
          : `${placeholder} (${selectedSet.size})`)
    : (value || placeholder);

  const hasValue = multi ? selectedSet.size > 0 : !!value;

  const listboxId = useMemo(() => {
    return `${label.toLowerCase().replace(/[^a-z0-9_-]/g, '-')}-listbox`;
  }, [label]);

  return (
    <div className="relative w-full" ref={dropdownRef}>
      {showLabel && <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>}
      <button
        type="button"
        onClick={() => isOpen ? setIsOpen(false) : handleOpenDropdown()}
        disabled={disabled}
        className="w-full inline-flex items-center justify-between gap-1.5 h-8 px-2.5 bg-white border border-slate-300 rounded-lg text-sm font-medium hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0014cc]/20 focus:border-[#0014cc] transition-colors overflow-hidden disabled:bg-slate-50/70 disabled:text-slate-600 disabled:cursor-not-allowed"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={listboxId}
      >
        <span className="flex items-center gap-2 min-w-0 flex-1 overflow-hidden">
          {!hasValue && icon}
          {multi && hasValue && renderMultiDisplay ? (
            <span className="min-w-0 flex-1 truncate text-left text-slate-800">
              {renderMultiDisplay([...selectedSet])}
            </span>
          ) : hasValue && renderOption ? (
            <span className="min-w-0 flex-1 truncate text-left text-slate-800">
              {multi && selectedSet.size === 1
                ? renderOption([...selectedSet][0])
                : (!multi && value ? renderOption(value) : displayValue)}
            </span>
          ) : (
            <span className={`min-w-0 flex-1 truncate text-left ${hasValue ? 'text-slate-800' : 'text-slate-400'}`}>
              {displayValue}
            </span>
          )}
        </span>
        <svg
          className={`w-3.5 h-3.5 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''} text-slate-400`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown */}
          <div
            id={listboxId}
            role="listbox"
            aria-label={`${label} options`}
            aria-multiselectable={multi || undefined}
            className="absolute left-0 mt-2 min-w-full w-max max-w-[320px] bg-white border border-gray-200 rounded-lg shadow-lg z-20"
          >
            {/* Search input */}
            {searchable && (
              <div className="p-2 border-b border-gray-100">
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search..."
                  className="w-full px-3 py-1.5 text-sm bg-white border border-slate-200 rounded focus:ring-2 focus:ring-[#0014cc]/20 focus:border-[#0014cc] outline-none"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            )}

            <div className="max-h-60 overflow-y-auto py-1">
            {hasValue && (
              <button
                type="button"
                onClick={() => {
                  onChange('');
                  if (!multi) {
                    setIsOpen(false);
                    setFocusedIndex(-1);
                  }
                }}
                className="w-full px-4 py-1.5 text-left text-xs font-medium text-[#0014cc] hover:bg-slate-50 border-b border-slate-100"
              >
                Clear all
              </button>
            )}
            {/* Options — clicking the active option deselects it */}
            {allOptions.length === 0 ? (
              <div className="px-4 py-3 text-xs text-slate-400 text-center">
                No matching options
              </div>
            ) : (
              allOptions.map((option, index) => {
                const selected = isSelected(option);
                return (
                  <button
                    key={option}
                    role="option"
                    aria-selected={selected}
                    onClick={() => handleSelect(option)}
                    className={`w-full text-left px-4 py-2 transition-colors ${
                      focusedIndex === index
                        ? 'bg-slate-100'
                        : 'hover:bg-slate-50'
                    } ${
                      selected
                        ? 'bg-[#0014cc]/5 text-[#0014cc] font-medium'
                        : 'text-slate-700'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate">{renderOption ? renderOption(option) : option}</span>
                      {selected && (
                        <svg
                          className="w-4 h-4 text-[#0014cc] shrink-0"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </div>
                  </button>
                );
              })
            )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
